import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { MaskedLines, Reveal, SectionHead } from "@/components/Reveal";
import CtaBanner from "@/components/CtaBanner";
import { PROGRAMS, DAY_TIMELINE, PRINCIPLES, IMAGES } from "@/data/content";

const Method = () => (
  <motion.main
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.5 }}
    data-testid="method-page"
    className="pt-16"
  >
    <section className="relative overflow-hidden py-24 sm:py-32">
      <div
        className="pointer-events-none absolute -left-40 top-0 h-[460px] w-[460px] rounded-full blur-[150px]"
        style={{ background: "radial-gradient(circle, rgba(204,10,20,0.16), transparent 65%)" }}
      />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-jb text-xs uppercase tracking-[0.3em] text-[#CC0A14]"
        >
          The Method — Published in Full
        </motion.p>
        <MaskedLines
          lines={["TWO PILLARS.", "ZERO COMPROMISE."]}
          className="mt-6 font-syne text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-[1.02] tracking-tight text-white"
        />
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-lg"
        >
          Everything the club runs, written down. No gated PDFs, no supplements
          funnel. Read it, take the side-profile photo, and start tomorrow
          morning.
        </motion.p>
      </div>
    </section>

    {PROGRAMS.map((p, i) => (
      <section
        key={p.id}
        data-testid={`method-pillar-${p.id}`}
        className="border-t border-[#27272A] py-20 sm:py-28"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
            <div className="relative border border-[#27272A]">
              <div className="absolute -left-2.5 -top-2.5 z-10 h-6 w-6 border-l-2 border-t-2 border-[#CC0A14]" />
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={IMAGES[p.image]}
                  alt={p.title}
                  loading="lazy"
                  className="h-full w-full object-cover grayscale"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/80 via-transparent to-transparent" />
              </div>
              <div className="flex items-center justify-between border-t border-[#27272A] bg-[#0c0c0e] px-4 py-3">
                <span className="font-jb text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                  Pillar {p.index}
                </span>
                <span className="font-jb text-[10px] uppercase tracking-[0.22em] text-[#CC0A14]">
                  {p.tag}
                </span>
              </div>
            </div>
          </Reveal>
          <div className={i % 2 === 1 ? "lg:order-1" : ""}>
            <Reveal>
              <p className="font-syne text-6xl font-extrabold text-outline sm:text-7xl">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h2 className="mt-4 font-syne text-2xl font-bold tracking-tight text-white sm:text-3xl">
                {p.title}
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-zinc-400 sm:text-base">
                {p.description}
              </p>
            </Reveal>
            <div className="mt-8 space-y-4">
              {p.points.map((pt, j) => (
                <Reveal key={pt} delay={j * 0.06}>
                  <div className="flex items-start gap-4 border border-[#27272A] bg-[#121214] px-5 py-4">
                    <Check size={16} className="mt-0.5 shrink-0 text-[#CC0A14]" />
                    <span className="text-sm leading-relaxed text-zinc-300">{pt}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    ))}

    <section
      data-testid="method-day-section"
      className="border-t border-[#27272A] bg-[#0a0a0c] py-20 sm:py-28"
    >
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <SectionHead
          eyebrow="A Day on Protocol"
          title="What Tuesday actually looks like."
          testId="method-day-heading"
        />
        <div className="mt-14 border-l border-[#27272A]">
          {DAY_TIMELINE.map((d, i) => (
            <Reveal key={d.time} delay={i * 0.06}>
              <div className="relative pb-12 pl-10 last:pb-0">
                <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 bg-[#CC0A14]" />
                <p className="font-jb text-xs tracking-[0.25em] text-[#CC0A14]">{d.time}</p>
                <h3 className="mt-2 font-syne text-lg font-bold tracking-tight text-white">
                  {d.title}
                </h3>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-zinc-400">{d.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="method-principles" className="border-t border-[#27272A] py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead
          eyebrow="House Rules"
          title="Six laws. Memorise them by week two."
          testId="method-principles-heading"
        />
        <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PRINCIPLES.map((pr, i) => (
            <Reveal key={pr} delay={i * 0.05}>
              <div
                data-testid={`principle-${i}`}
                className="group flex items-center gap-5 border border-[#27272A] bg-[#121214] p-6 transition-colors duration-300 hover:border-[#7F1D1D]"
              >
                <span className="font-syne text-2xl font-extrabold text-outline transition-colors duration-300 group-hover:text-[#CC0A14] group-hover:[-webkit-text-stroke:0px]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-syne text-base font-bold tracking-tight text-zinc-200">
                  {pr}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CtaBanner />
  </motion.main>
);

export default Method;
